import React, { useId, useState } from 'react';
import PropTypes from 'prop-types';

import { FormActions } from '../form_layout';
import '../form_layout/form_layout.css';
import './request_time_warning.css';
import {
  ACK_NOTE_MAX,
  WARNING_CODES,
  ack_note_error,
  build_request_time_ack,
  warning_view
} from './request_time_warning_rules';

// The in-dialog step shown instead of the form when a request time looks wrong (A-52): Ops either
// acknowledge it (with a note for BEFORE_BOOKING) and save, or go back and change the time.
export default function RequestTimeWarningStep({
  warning,
  requestedLocal,
  busy,
  onAcknowledge,
  onChangeTime
}) {
  const [note, setNote] = useState('');
  const [touched, setTouched] = useState(false);
  const noteId = useId();
  const titleId = useId();
  const view = warning_view(warning, requestedLocal);
  const error = touched ? ack_note_error(warning.warning, note) : '';

  const acknowledge = () => {
    setTouched(true);
    if (busy || ack_note_error(warning.warning, note)) return;
    onAcknowledge(build_request_time_ack(warning.warning, note));
  };

  return (
    <section className="rt-warning" aria-labelledby={titleId} data-testid="request-time-warning">
      <h5 id={titleId}>{view.title}</h5>
      <div className="rt-warning-box" role="alert">
        <p className="rt-warning-message">{view.message}</p>
        <ul className="rt-warning-facts">
          {view.requested_text ? <li>{view.requested_text}</li> : null}
          {view.booked_text ? <li>{view.booked_text}</li> : null}
          {view.start_text ? <li>{view.start_text}</li> : null}
        </ul>
        <p className="rt-warning-help">
          {view.note_required
            ? 'Saving records your acknowledgement and your note, and flags this class for review.'
            : 'Saving records your acknowledgement with the change.'}
        </p>
      </div>
      <div className="myt-field">
        <label htmlFor={noteId} className="myt-form-label">
          {view.note_required ? 'Why is this time right? (required)' : 'Note (optional)'}
        </label>
        <textarea
          id={noteId}
          className="required-reason-input rt-warning-note"
          value={note}
          maxLength={ACK_NOTE_MAX}
          aria-required={view.note_required ? 'true' : 'false'}
          aria-invalid={error ? 'true' : undefined}
          aria-describedby={error ? `${noteId}-error` : undefined}
          placeholder="e.g. Asked on WhatsApp before the class was rebooked"
          style={{ height: '84px' }}
          onChange={(e) => setNote(e.target.value)}
        />
        <p className="myt-form-help">
          {note.trim().length}/{ACK_NOTE_MAX}
        </p>
        {error ? (
          <p id={`${noteId}-error`} className="myt-form-error">
            * {error}
          </p>
        ) : null}
      </div>
      <FormActions>
        <button type="button" className="button-secondary" disabled={busy} onClick={onChangeTime}>
          Change time
        </button>
        <button type="button" disabled={busy} onClick={acknowledge}>
          {busy ? 'Saving…' : 'Acknowledge and save'}
        </button>
      </FormActions>
    </section>
  );
}

RequestTimeWarningStep.propTypes = {
  warning: PropTypes.shape({
    warning: PropTypes.oneOf(WARNING_CODES).isRequired,
    booked_at: PropTypes.string,
    class_start: PropTypes.string,
    message: PropTypes.string
  }).isRequired,
  requestedLocal: PropTypes.string,
  busy: PropTypes.bool,
  onAcknowledge: PropTypes.func.isRequired,
  onChangeTime: PropTypes.func.isRequired
};

RequestTimeWarningStep.defaultProps = { requestedLocal: '', busy: false };
